/**
 * ScheduleReportDialog — wizard to create/edit a scheduled report.
 * Used from both the ScheduledReportsPage and the "Schedule This" button in ModuleReports.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Calendar, Clock, Mail, Plus, X } from 'lucide-react';
import {
  ASSET_REPORTS, INVOICE_REPORTS, GRN_REPORTS, PO_REPORTS,
  RFX_REPORTS, REQUISITION_REPORTS, CONTRACT_REPORTS, ACCOUNT_CODE_REPORTS,
} from './reportConfigs';

const ALL_MODULES = [
  { value: 'assets', label: 'Asset Management', reports: ASSET_REPORTS },
  { value: 'invoices', label: 'Invoice Matching', reports: INVOICE_REPORTS },
  { value: 'grn', label: 'Receiving', reports: GRN_REPORTS },
  { value: 'orders', label: 'Purchase Orders', reports: PO_REPORTS },
  { value: 'rfx', label: 'RFx Sourcing', reports: RFX_REPORTS },
  { value: 'requisitions', label: 'Requisitions', reports: REQUISITION_REPORTS },
  { value: 'contracts', label: 'Contracts', reports: CONTRACT_REPORTS },
  { value: 'account-codes', label: 'Account Codes', reports: ACCOUNT_CODE_REPORTS },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' }, { value: 2, label: 'Tuesday' }, { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' }, { value: 5, label: 'Friday' }, { value: 6, label: 'Saturday' }, { value: 7, label: 'Sunday' },
];

export default function ScheduleReportDialog({ open, onClose, onSaved, prefill }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [systemTz, setSystemTz] = useState('UTC');

  useEffect(() => {
    if (open) {
      api.get('/api/system/settings').then(r => {
        if (r.data?.timezone) setSystemTz(r.data.timezone);
      }).catch(() => {});
    }
  }, [open]);

  const [form, setForm] = useState({
    name: '',
    module: '',
    report_id: '',
    report_label: '',
    module_label: '',
    filters: {},
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    time_of_day: '08:00',
    recipients: [''],
  });

  useEffect(() => {
    if (open && prefill) {
      setForm(f => ({
        ...f,
        module: prefill.module || '',
        report_id: prefill.report_id || '',
        report_label: prefill.report_label || '',
        module_label: prefill.module_label || '',
        filters: prefill.filters || {},
        name: prefill.name || `${prefill.module_label || ''} — ${prefill.report_label || ''}`,
        recipients: prefill.recipients || [''],
        frequency: prefill.frequency || 'weekly',
        day_of_week: prefill.day_of_week || 1,
        day_of_month: prefill.day_of_month || 1,
        time_of_day: prefill.time_of_day || '08:00',
      }));
      setStep(prefill.module && prefill.report_id ? 2 : 1);
    } else if (open) {
      setForm({ name: '', module: '', report_id: '', report_label: '', module_label: '', filters: {}, frequency: 'weekly', day_of_week: 1, day_of_month: 1, time_of_day: '08:00', recipients: [''] });
      setStep(1);
    }
  }, [open, prefill]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedModule = ALL_MODULES.find(m => m.value === form.module);
  const selectedReport = selectedModule?.reports?.find(r => r.id === form.report_id);

  const handleModuleSelect = (mod) => {
    set('module', mod.value);
    set('module_label', mod.label);
    set('report_id', '');
    set('report_label', '');
  };

  const handleReportSelect = (r) => {
    set('report_id', r.id);
    set('report_label', r.label);
    set('name', `${form.module_label} — ${r.label}`);
    setStep(2);
  };

  const addRecipient = () => set('recipients', [...form.recipients, '']);
  const removeRecipient = (i) => set('recipients', form.recipients.filter((_, idx) => idx !== i));
  const updateRecipient = (i, v) => {
    const next = [...form.recipients];
    next[i] = v;
    set('recipients', next);
  };

  const setFilter = (k, v) => set('filters', { ...form.filters, [k]: v });

  const handleSave = async () => {
    const validRecipients = form.recipients.filter(r => r.trim());
    if (!validRecipients.length) { toast.error('Add at least one recipient email'); return; }
    if (!form.module || !form.report_id) { toast.error('Select a module and report'); return; }
    if (!form.name.trim()) { toast.error('Provide a schedule name'); return; }

    setSaving(true);
    try {
      const payload = { ...form, recipients: validRecipients };
      if (prefill?.id) {
        await api.put(`/api/scheduled-reports/${prefill.id}`, payload);
        toast.success('Schedule updated');
      } else {
        await api.post('/api/scheduled-reports/', payload);
        toast.success('Schedule created');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to save schedule'));
    }
    setSaving(false);
  };

  const maxStep = 3;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="schedule-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" /> {prefill?.id ? 'Edit Schedule' : 'Schedule a Report'}
          </DialogTitle>
          <div className="flex gap-1 mt-2">
            {[1, 2, 3].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full ${step >= s ? 'bg-primary' : 'bg-muted'}`} />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Step {step} of {maxStep}: {step === 1 ? 'Select Report' : step === 2 ? 'Schedule & Filters' : 'Recipients & Confirm'}
          </p>
        </DialogHeader>

        {/* Step 1 — Select Module & Report */}
        {step === 1 && (
          <div className="space-y-4" data-testid="schedule-step-1">
            <div>
              <Label className="text-xs font-semibold mb-2 block">Module</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_MODULES.map(m => (
                  <button key={m.value} onClick={() => handleModuleSelect(m)}
                    className={`p-3 text-left border rounded-lg text-sm transition-colors ${form.module === m.value ? 'border-primary bg-primary/5 font-semibold' : 'hover:bg-accent/30'}`}
                    data-testid={`schedule-module-${m.value}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {selectedModule && (
              <div>
                <Label className="text-xs font-semibold mb-2 block">Report</Label>
                <div className="grid grid-cols-1 gap-2">
                  {selectedModule.reports.map(r => (
                    <button key={r.id} onClick={() => handleReportSelect(r)}
                      className={`flex items-center gap-3 p-3 text-left border rounded-lg text-sm hover:bg-accent/20 transition-colors ${form.report_id === r.id ? 'border-primary bg-primary/5' : ''}`}
                      data-testid={`schedule-report-${r.id}`}>
                      <r.icon className="h-4 w-4 text-primary flex-shrink-0" />
                      <div>
                        <div className="font-medium">{r.label}</div>
                        <div className="text-[10px] text-muted-foreground">{r.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2 — Schedule Frequency & Filters */}
        {step === 2 && (
          <div className="space-y-4" data-testid="schedule-step-2">
            <div>
              <Label className="text-xs font-semibold">Schedule Name</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} className="mt-1" data-testid="schedule-name" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold">Frequency</Label>
                <select value={form.frequency} onChange={e => set('frequency', e.target.value)}
                  className="w-full h-9 border rounded-md px-2 text-sm mt-1" data-testid="schedule-frequency">
                  {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Time ({systemTz})</Label>
                <Input type="time" value={form.time_of_day} onChange={e => set('time_of_day', e.target.value)} className="mt-1" data-testid="schedule-time" />
              </div>
            </div>

            {form.frequency === 'weekly' && (
              <div>
                <Label className="text-xs font-semibold">Day of Week</Label>
                <select value={form.day_of_week} onChange={e => set('day_of_week', parseInt(e.target.value))}
                  className="w-full h-9 border rounded-md px-2 text-sm mt-1" data-testid="schedule-dow">
                  {DAYS_OF_WEEK.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
            )}

            {form.frequency === 'monthly' && (
              <div>
                <Label className="text-xs font-semibold">Day of Month</Label>
                <Input type="number" min={1} max={28} value={form.day_of_month} onChange={e => set('day_of_month', parseInt(e.target.value) || 1)}
                  className="mt-1" data-testid="schedule-dom" />
              </div>
            )}

            {/* Filters from report config */}
            {selectedReport?.filters && selectedReport.filters.length > 0 && (
              <div>
                <Label className="text-xs font-semibold mb-2 block">Report Criteria / Filters</Label>
                <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg border">
                  {selectedReport.filters.map(f => (
                    <div key={f.key}>
                      <Label className="text-[10px] text-muted-foreground">{f.label}</Label>
                      {f.type === 'select' ? (
                        <select value={form.filters[f.key] || ''} onChange={e => setFilter(f.key, e.target.value)}
                          className="w-full h-7 border rounded px-2 text-xs mt-0.5" data-testid={`schedule-filter-${f.key}`}>
                          <option value="">All</option>
                          {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : (
                        <Input type={f.type || 'text'} value={form.filters[f.key] || ''}
                          onChange={e => setFilter(f.key, e.target.value)}
                          className="h-7 text-xs mt-0.5" placeholder={f.placeholder || ''}
                          data-testid={`schedule-filter-${f.key}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3 — Recipients & Confirm */}
        {step === 3 && (
          <div className="space-y-4" data-testid="schedule-step-3">
            <div className="p-3 bg-muted/30 rounded-lg border space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Module</span>
                <span className="font-medium">{form.module_label}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Report</span>
                <span className="font-medium">{form.report_label}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Frequency</span>
                <Badge variant="outline" className="text-[10px]">{form.frequency}{form.frequency === 'weekly' ? ` (${DAYS_OF_WEEK.find(d => d.value === form.day_of_week)?.label || ''})` : form.frequency === 'monthly' ? ` (day ${form.day_of_month})` : ''} at {form.time_of_day} {systemTz}</Badge>
              </div>
              {Object.entries(form.filters).filter(([, v]) => v).length > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Filters</span>
                  <span className="font-mono text-[10px]">{Object.entries(form.filters).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(', ')}</span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs font-semibold flex items-center gap-1.5 mb-2">
                <Mail className="h-3.5 w-3.5" /> Email Recipients
              </Label>
              <div className="space-y-2">
                {form.recipients.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="email" value={r} onChange={e => updateRecipient(i, e.target.value)} placeholder="email@company.com"
                      className="flex-1 h-8 text-sm" data-testid={`schedule-recipient-${i}`} />
                    {form.recipients.length > 1 && (
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeRecipient(i)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addRecipient} data-testid="schedule-add-recipient">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Recipient
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <div>
            {step > 1 && <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)}>Back</Button>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            {step < maxStep ? (
              <Button size="sm" onClick={() => setStep(s => s + 1)} disabled={step === 1 && (!form.module || !form.report_id)} data-testid="schedule-next-btn">
                Next
              </Button>
            ) : (
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="schedule-save-btn">
                {saving ? 'Saving...' : (prefill?.id ? 'Update Schedule' : 'Create Schedule')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
